import { useState } from "react";
import { updatePortfolio, uploadFlexXml, fetchFlexFromIbkr } from "../api/client";
import type { Portfolio } from "../api/client";

interface Props {
  portfolio: Portfolio | null;
  onRename: (id: number, newName: string) => Promise<void>;
  onClearPositions: (id: number) => Promise<void>;
  onClearTrades: (id: number) => Promise<void>;
  onDelete: (id: number, name: string) => Promise<void>;
  onLogout: () => void;
  onCreatePortfolio: () => void;
}

interface ActionState {
  loading: boolean;
  error: string | null;
  success: string | null;
}

const initState = (): ActionState => ({ loading: false, error: null, success: null });

export default function PortfolioSettingsPage({
  portfolio,
  onRename,
  onClearPositions,
  onClearTrades,
  onDelete,
  onLogout,
  onCreatePortfolio,
}: Props) {
  const [renameState, setRenameState] = useState<ActionState>(initState());
  const [renameValue, setRenameValue] = useState("");
  const [clearPosState, setClearPosState] = useState<ActionState>(initState());
  const [clearTrdState, setClearTrdState] = useState<ActionState>(initState());
  const [deleteState, setDeleteState] = useState<ActionState>(initState());
  const [ibkrState, setIbkrState] = useState<ActionState>(initState());
  const [ibkrToken, setIbkrToken] = useState(portfolio?.ibkr_flex_token ?? "");
  const [ibkrQueryId, setIbkrQueryId] = useState(portfolio?.ibkr_flex_query_id ?? "");
  const [flexFile, setFlexFile] = useState<File | null>(null);
  const [uploadState, setUploadState] = useState<ActionState>(initState());
  const [fetchIbkrState, setFetchIbkrState] = useState<ActionState>(initState());

  const sectionStyleBase: React.CSSProperties = {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "20px 24px",
    marginBottom: 20,
  };

  const labelStyleBase: React.CSSProperties = {
    display: "block",
    fontWeight: 600,
    color: "#1e293b",
    marginBottom: 4,
    fontSize: 15,
  };

  const descStyleBase: React.CSSProperties = {
    color: "#64748b",
    fontSize: 13,
    marginBottom: 14,
  };

  const btnStyleBase = (variant: "primary" | "danger" | "warn"): React.CSSProperties => ({
    padding: "8px 18px",
    borderRadius: 6,
    border: "none",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 14,
    background:
      variant === "primary" ? "#3b82f6" : variant === "danger" ? "#ef4444" : "#f59e0b",
    color: "#fff",
  });

  if (!portfolio) {
    return (
      <div style={{ maxWidth: 640, margin: "0 auto", padding: "24px 16px" }}>
        <h2 style={{ margin: "0 0 24px", color: "#1e293b", fontSize: 22 }}>⚙️ Settings</h2>
        <div style={sectionStyleBase}>
          <span style={labelStyleBase}>Portfolios</span>
          <p style={descStyleBase}>Create a new portfolio to track a separate brokerage account.</p>
          <button style={btnStyleBase("primary")} onClick={onCreatePortfolio}>+ New Portfolio</button>
        </div>
        <div style={sectionStyleBase}>
          <span style={labelStyleBase}>Account</span>
          <p style={descStyleBase}>Sign out of your account.</p>
          <button style={{ ...btnStyleBase("primary"), background: "#64748b" }} onClick={onLogout}>Sign out</button>
        </div>
        <div style={{ color: "#94a3b8", fontSize: 13, padding: "8px 0" }}>Select a portfolio from the top bar to manage its settings.</div>
      </div>
    );
  }

  const run = async (
    setState: React.Dispatch<React.SetStateAction<ActionState>>,
    action: () => Promise<void>,
    successMsg: string
  ) => {
    setState({ loading: true, error: null, success: null });
    try {
      await action();
      setState({ loading: false, error: null, success: successMsg });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } }; message?: string })?.response?.data
          ?.detail ?? "An error occurred.";
      setState({ loading: false, error: msg, success: null });
    }
  };

  const sectionStyle = sectionStyleBase;
  const labelStyle = labelStyleBase;
  const descStyle = descStyleBase;
  const btnStyle = btnStyleBase;

  const feedbackStyle = (state: ActionState): React.CSSProperties => ({
    marginTop: 8,
    fontSize: 13,
    color: state.error ? "#ef4444" : "#16a34a",
  });

  return (
    <div style={{ maxWidth: 640, margin: "0 auto", padding: "24px 16px" }}>
      <h2 style={{ margin: "0 0 24px", color: "#1e293b", fontSize: 22 }}>⚙️ Settings</h2>

      {/* Create portfolio */}
      <div style={sectionStyle}>
        <span style={labelStyle}>Portfolios</span>
        <p style={descStyle}>Create a new portfolio to track a separate brokerage account.</p>
        <button style={btnStyle("primary")} onClick={onCreatePortfolio}>+ New Portfolio</button>
      </div>

      {/* Info */}
      <div style={sectionStyle}>
        <span style={labelStyle}>{portfolio.name}</span>
        <div style={{ color: "#64748b", fontSize: 13 }}>
          Account ID: <strong>{portfolio.account_id}</strong> &nbsp;·&nbsp; Currency:{" "}
          <strong>{portfolio.currency}</strong>
        </div>
      </div>

      {/* Rename */}
      <div style={sectionStyle}>
        <span style={labelStyle}>Rename portfolio</span>
        <p style={descStyle}>Update the display name for this portfolio.</p>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            type="text"
            defaultValue={portfolio.name}
            key={portfolio.id}
            onChange={(e) => setRenameValue(e.target.value)}
            placeholder={portfolio.name}
            style={{
              flex: 1,
              padding: "8px 12px",
              border: "1px solid #d1d5db",
              borderRadius: 6,
              fontSize: 14,
              color: "#111827",
              background: "#f9fafb",
              outline: "none",
            }}
          />
          <button
            style={btnStyle("primary")}
            disabled={renameState.loading}
            onClick={() => {
              const newName = (renameValue || "").trim() || portfolio.name;
              if (newName === portfolio.name) return;
              run(setRenameState, () => onRename(portfolio.id, newName), "Portfolio renamed.");
            }}
          >
            {renameState.loading ? "Renaming…" : "Rename"}
          </button>
        </div>
        {(renameState.error || renameState.success) && (
          <div style={feedbackStyle(renameState)}>
            {renameState.error ?? renameState.success}
          </div>
        )}
      </div>

      {/* IBKR Flex credentials */}
      <div style={sectionStyle}>
        <span style={labelStyle}>IBKR Flex credentials</span>
        <p style={descStyle}>
          Per-portfolio token and query ID for the IBKR Flex Web Service. These take
          precedence over the global environment variables and are used by the IBKR Sync
          button in the top bar.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 12 }}>
          <div>
            <label style={{ fontSize: 13, color: "#475569", fontWeight: 500, display: "block", marginBottom: 4 }}>Flex Token</label>
            <input
              type="password"
              key={`token-${portfolio.id}`}
              value={ibkrToken}
              onChange={(e) => setIbkrToken(e.target.value)}
              placeholder="e.g. 150493154013645088056134"
              autoComplete="off"
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #d1d5db",
                borderRadius: 6,
                fontSize: 14,
                color: "#111827",
                background: "#f9fafb",
                outline: "none",
                boxSizing: "border-box",
              }}
            />
          </div>
          <div>
            <label style={{ fontSize: 13, color: "#475569", fontWeight: 500, display: "block", marginBottom: 4 }}>Query ID</label>
            <input
              type="text"
              key={`qid-${portfolio.id}`}
              value={ibkrQueryId}
              onChange={(e) => setIbkrQueryId(e.target.value)}
              placeholder="e.g. 1410941"
              autoComplete="off"
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #d1d5db",
                borderRadius: 6,
                fontSize: 14,
                color: "#111827",
                background: "#f9fafb",
                outline: "none",
                boxSizing: "border-box",
              }}
            />
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            style={btnStyle("primary")}
            disabled={ibkrState.loading}
            onClick={() =>
              run(
                setIbkrState,
                () =>
                  updatePortfolio(portfolio.id, {
                    ibkr_flex_token: ibkrToken.trim() || null,
                    ibkr_flex_query_id: ibkrQueryId.trim() || null,
                  }).then(() => {}),
                "Credentials saved."
              )
            }
          >
            {ibkrState.loading ? "Saving…" : "Save credentials"}
          </button>
          <button
            style={{ ...btnStyle("warn"), background: "#64748b" }}
            disabled={ibkrState.loading}
            onClick={() => {
              setIbkrToken("");
              setIbkrQueryId("");
              run(
                setIbkrState,
                () =>
                  updatePortfolio(portfolio.id, {
                    ibkr_flex_token: null,
                    ibkr_flex_query_id: null,
                  }).then(() => {}),
                "Credentials cleared."
              );
            }}
          >
            Clear
          </button>
        </div>
        {(ibkrState.error || ibkrState.success) && (
          <div style={feedbackStyle(ibkrState)}>{ibkrState.error ?? ibkrState.success}</div>
        )}
      </div>

      {/* Upload Flex XML */}
      <div style={sectionStyle}>
        <span style={labelStyle}>Upload IBKR Flex Query XML</span>
        <p style={descStyle}>Upload a Flex Query XML file exported from IBKR. Imports all trades and open positions.</p>
        <input
          type="file"
          accept=".xml"
          onChange={(e) => setFlexFile(e.target.files?.[0] ?? null)}
          style={{ marginBottom: 12, display: "block" }}
        />
        <button
          style={btnStyle(!flexFile || uploadState.loading ? "warn" : "primary")}
          disabled={!flexFile || uploadState.loading}
          onClick={async () => {
            if (!portfolio || !flexFile) return;
            setUploadState({ loading: true, error: null, success: null });
            try {
              const res = await uploadFlexXml(portfolio.id, flexFile);
              const d = res.data as Record<string, number>;
              setUploadState({ loading: false, error: null, success: `✅ ${d.trades_added} trades added, ${d.trades_skipped} skipped, ${d.positions_added} positions added, ${d.positions_updated} updated.` });
              setFlexFile(null);
            } catch (err: unknown) {
              const msg = (err as { response?: { data?: { detail?: string } }; message?: string })?.response?.data?.detail ?? "Upload failed.";
              setUploadState({ loading: false, error: msg, success: null });
            }
          }}
        >
          {uploadState.loading ? "Uploading…" : "Upload & Import"}
        </button>
        {(uploadState.error || uploadState.success) && (
          <div style={feedbackStyle(uploadState)}>{uploadState.error ?? uploadState.success}</div>
        )}
      </div>

      {/* Fetch from IBKR */}
      <div style={sectionStyle}>
        <span style={labelStyle}>Fetch from IBKR Flex Web Service</span>
        <p style={descStyle}>Trigger a remote Flex Query using the credentials saved above.</p>
        <button
          style={btnStyle(fetchIbkrState.loading ? "warn" : "primary")}
          disabled={fetchIbkrState.loading}
          onClick={async () => {
            if (!portfolio) return;
            setFetchIbkrState({ loading: true, error: null, success: null });
            try {
              const res = await fetchFlexFromIbkr(portfolio.id);
              const d = res.data as Record<string, number>;
              setFetchIbkrState({ loading: false, error: null, success: `✅ ${d.trades_added} trades added, ${d.trades_skipped} skipped.` });
            } catch (err: unknown) {
              const msg = (err as { response?: { data?: { detail?: string } }; message?: string })?.response?.data?.detail ?? "Fetch failed.";
              setFetchIbkrState({ loading: false, error: msg, success: null });
            }
          }}
        >
          {fetchIbkrState.loading ? "Fetching…" : "Fetch from IBKR"}
        </button>
        {(fetchIbkrState.error || fetchIbkrState.success) && (
          <div style={feedbackStyle(fetchIbkrState)}>{fetchIbkrState.error ?? fetchIbkrState.success}</div>
        )}
      </div>
      <div style={{ ...sectionStyle, borderColor: "#fef3c7" }}>
        <span style={labelStyle}>Clear positions</span>
        <p style={descStyle}>
          Delete all position snapshots for this portfolio. Trades are not affected. This cannot be
          undone.
        </p>
        <button
          style={btnStyle("warn")}
          disabled={clearPosState.loading}
          onClick={() => {
            if (
              !confirm(
                `Delete all positions for "${portfolio.name}"? This cannot be undone.`
              )
            )
              return;
            run(setClearPosState, () => onClearPositions(portfolio.id), "All positions cleared.");
          }}
        >
          {clearPosState.loading ? "Clearing…" : "Clear positions"}
        </button>
        {(clearPosState.error || clearPosState.success) && (
          <div style={feedbackStyle(clearPosState)}>
            {clearPosState.error ?? clearPosState.success}
          </div>
        )}
      </div>

      {/* Clear trades */}
      <div style={{ ...sectionStyle, borderColor: "#fef3c7" }}>
        <span style={labelStyle}>Clear trades</span>
        <p style={descStyle}>
          Delete all trade records for this portfolio. Position snapshots are not affected. This
          cannot be undone.
        </p>
        <button
          style={btnStyle("warn")}
          disabled={clearTrdState.loading}
          onClick={() => {
            if (
              !confirm(`Delete all trades for "${portfolio.name}"? This cannot be undone.`)
            )
              return;
            run(setClearTrdState, () => onClearTrades(portfolio.id), "All trades cleared.");
          }}
        >
          {clearTrdState.loading ? "Clearing…" : "Clear trades"}
        </button>
        {(clearTrdState.error || clearTrdState.success) && (
          <div style={feedbackStyle(clearTrdState)}>
            {clearTrdState.error ?? clearTrdState.success}
          </div>
        )}
      </div>

      {/* Sign out */}
      <div style={sectionStyle}>
        <span style={labelStyle}>Account</span>
        <p style={descStyle}>Sign out of your account.</p>
        <button
          style={{ ...btnStyle("primary"), background: "#64748b" }}
          onClick={onLogout}
        >
          Sign out
        </button>
      </div>

      {/* Delete portfolio */}
      <div style={{ ...sectionStyle, borderColor: "#fecaca" }}>
        <span style={{ ...labelStyle, color: "#ef4444" }}>Delete portfolio</span>
        <p style={descStyle}>
          Permanently remove this portfolio along with all its trades, positions, and benchmarks.
          This cannot be undone.
        </p>
        <button
          style={btnStyle("danger")}
          disabled={deleteState.loading}
          onClick={() => {
            if (
              !confirm(
                `Delete portfolio "${portfolio.name}"? This will permanently remove all its data.`
              )
            )
              return;
            run(setDeleteState, () => onDelete(portfolio.id, portfolio.name), "Portfolio deleted.");
          }}
        >
          {deleteState.loading ? "Deleting…" : "Delete portfolio"}
        </button>
        {(deleteState.error || deleteState.success) && (
          <div style={feedbackStyle(deleteState)}>
            {deleteState.error ?? deleteState.success}
          </div>
        )}
      </div>
    </div>
  );
}
